import React from 'react';

import { Minus } from 'lucide-react';

import {
  isActionAddPostCategory,
  isActionAddPostTags,
  isActionAddToRoadmap,
  isActionApplyPostStatus,
} from 'common/automations/actionUtils';
import stringSort from 'common/util/stringSort';
import unique from 'common/util/unique';

import { actionLabels, findActiveOption } from '../util';
import AddPostCategory from './AddPostCategory';
import AddPostTags from './AddPostTags';
import AddToRoadmap from './AddToRoadmap';
import ApplyPostStatus from './ApplyPostStatus';

import type { Action, ActionType, Filter } from 'common/automations/constants';
import Button from 'common/uiV3/Button';
import HStack from 'common/uiV3/HStack';
import { Select } from 'common/uiV3/Select';

export interface FormAction extends Action {
  id: string;
}

type ActionRowProps = {
  action: FormAction;
  allowedActions: ActionType[];
  filters: Filter[];
  onActionChange: (properties: FormAction['properties']) => void;
  onActionTypeChange: (id: string, actionType: ActionType) => void;
  onRemove: (id: string) => void;
};

const ActionRow = ({
  action,
  allowedActions,
  filters,
  onActionChange,
  onActionTypeChange,
  onRemove,
}: ActionRowProps) => {
  // Allowed actions, plus the currently selected action
  const actionTypeDropdownOptions = unique([...allowedActions, action.type])
    .map((actionType) => ({
      label: actionLabels[actionType],
      value: actionType,
    }))
    .sort(stringSort('label', 'asc', true));

  return (
    <HStack className="actionRow grid grid-cols-[1fr_1fr_30px] gap-2">
      <div className="grid1">
        <Select
          options={actionTypeDropdownOptions}
          // Assert these types, since the select is not clearable
          onValueChange={(option) => option && onActionTypeChange(action.id, option as ActionType)}
          placeholder="Select an action"
          value={findActiveOption(actionTypeDropdownOptions, action.type)?.value}
          fullWidth
        />
      </div>
      <ActionField
        key={action.id}
        filters={filters}
        type={action.type}
        properties={action.properties}
        onChange={onActionChange}
      />
      <Button
        className="grid3"
        variant="plain"
        iconOnly
        aria-label="Remove action"
        onClick={() => onRemove(action.id)}>
        <Minus />
      </Button>
    </HStack>
  );
};

interface ActionFieldProps<A extends Action = Action> {
  filters: Filter[];
  type: A['type'];
  properties: A['properties'];
  onChange: <OcA extends Action = A>(properties?: OcA['properties']) => void;
}

const ActionField = ({ filters, ...props }: ActionFieldProps<Action>) => {
  if (isActionAddPostCategory(props)) {
    const { properties, onChange } = props;
    return <AddPostCategory filters={filters} properties={properties} onChange={onChange} />;
  } else if (isActionAddPostTags(props)) {
    const { properties, onChange } = props;
    return <AddPostTags filters={filters} properties={properties} onChange={onChange} />;
  } else if (isActionApplyPostStatus(props)) {
    const { properties, onChange } = props;
    return <ApplyPostStatus filters={filters} properties={properties} onChange={onChange} />;
  } else if (isActionAddToRoadmap(props)) {
    const { properties, onChange } = props;
    return <AddToRoadmap filters={filters} properties={properties} onChange={onChange} />;
  } else {
    return null;
  }
};

export default ActionRow;
